import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IApiQueryOptions } from 'api/ApiClient';
import { PageActions, PageHeader, PageHeaderAction, PageHeaderContent, PageLabel, PaginatedList, TabNav, TabNavItem } from 'components';
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { SiteSelectorAppearance } from 'components/SiteSelector/types';
import { useQueryString } from 'hooks';
import { routeConstants } from 'routes';
import { PermissionGate } from 'screens/Security';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee, setCurrentSite } from 'store/App';
import { Employees, getActionRequiredEmployeesCountRequest, getEmployeesRequest, selectEmployees, selectReviewRequiredEmployeesTotal } from 'store/Collections';
import { IStore } from 'store/types';
import { AffiliationType, EmployeeRole, EmployeesTab, IEmployerSite } from 'types';
import { ACTION_REQUIRED_FILTERS, DEFAULT_OFFSET, DEFAULT_PAGE_SIZE, DEFAULT_SORT, DEFAULT_STATUS_FILTERS, updateStatusFilters } from 'utils';
import UsersTab from './UsersTab';

const defaultStatusFilters = updateStatusFilters(DEFAULT_STATUS_FILTERS);

export const Users = ({ activeTab }: { activeTab: EmployeesTab }) => {
  const dispatch = useDispatch();
  const activeTabRef = useRef<EmployeesTab>(activeTab);
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const defaultFilters = activeTab === EmployeesTab.ALL ? defaultStatusFilters : ACTION_REQUIRED_FILTERS;
  const defaultQuery = { limit: DEFAULT_PAGE_SIZE, offset: DEFAULT_OFFSET, sort: DEFAULT_SORT, q: defaultFilters };
  const [urlQuery, setUrlQuery] = useQueryString('users', window.location, defaultQuery);

  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentOrganizationId = currentEmployee?.organizationId;
  const currentSiteId = currentEmployee?.siteId;

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));

  const { data: employees, total: totalEmployees } = useSelector(selectEmployees()) || { data: [], total: null };
  const requiresReviewCount = useSelector(selectReviewRequiredEmployeesTotal()) || 0;

  const affiliationType = currentSiteId ? AffiliationType.SITES : AffiliationType.ORGANIZATIONS;
  const affiliationId = currentSiteId || currentOrganizationId;

  const handleSiteSelect = (selected: IEmployerSite) => {
    dispatch(setCurrentSite(selected?.id));
    setUrlQuery(defaultQuery);
  };

  const fetchEmployees = useCallback(() => {
    if (affiliationId) {
      const options: IApiQueryOptions = { ...urlQuery, includeDismissed: true };
      dispatch(getEmployeesRequest(affiliationType, affiliationId, options));
    }
  }, [affiliationId, affiliationType, urlQuery]);

  useEffect(() => {
    fetchEmployees();
  }, [affiliationId, affiliationType, urlQuery]);

  // Fetch employees when the URL query changes
  useEffect(() => {
    if (affiliationId) dispatch(getActionRequiredEmployeesCountRequest(affiliationType, affiliationId));
  }, [dispatch, affiliationId, affiliationType]);

  // Reset the URL query when the active tab changes to avoid keeping the previous tab's filters
  useEffect(() => {
    if (activeTabRef.current === activeTab) return;
    activeTabRef.current = activeTab;
    setUrlQuery(defaultQuery);
  }, [activeTab]);

  return (
    <>
      <PageHeader>
        <PageLabel>Users</PageLabel>
        <PageHeaderContent>
          <ConnectedSiteSelector allSitesEnabled appearance={SiteSelectorAppearance.PAGE_HEADER} onSiteSelect={handleSiteSelect} />
          <PageActions>
            <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
              <PageHeaderAction appearance="tertiary" label="Add User" to={routeConstants.private.addEmployee} />
            </PermissionGate>
          </PageActions>
        </PageHeaderContent>
        <TabNav>
          <TabNavItem label="All Users" data-cy="tab-all" to={routeConstants.private.employees} isActive={activeTab === EmployeesTab.ALL} exact />
          <TabNavItem label="Needs Review" data-cy="tab-review" to={routeConstants.private.employeesNeedingApproval} isActive={activeTab === EmployeesTab.REVIEW} isUpdated={requiresReviewCount > 0} />
        </TabNav>
      </PageHeader>

      {(activeTab === EmployeesTab.ALL || activeTab === EmployeesTab.REVIEW) && (
        <PaginatedList total={totalEmployees} limit={urlQuery.limit} offset={urlQuery.offset} onPageChange={(options: IApiQueryOptions) => setUrlQuery(options)}>
          <UsersTab
            activeTab={activeTab}
            currentSiteId={currentSiteId}
            currentOrganizationId={currentOrganizationId}
            employees={employees}
            filterActive={filterActive}
            isLoading={isLoading}
            totalEmployees={totalEmployees}
            urlQuery={urlQuery}
            toggleFilterActive={setFilterActive}
            onQueryChange={(options: IApiQueryOptions) => setUrlQuery(options)}
            onConfirm={activeTab === EmployeesTab.REVIEW ? fetchEmployees : undefined}
          />
        </PaginatedList>
      )}
    </>
  );
};
